import React, { useEffect, useState } from 'react';
import { X, CreditCard, Wallet, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Annulation côté client : délai paramétrable + choix avoir compte / remboursement Stripe pour une résa payée. */
export default function CancelTeeTimeDialog({ booking, token, onClose, onCancelled }) {
  const [opts, setOpts] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/tee-times/portal/bookings/${booking.id}/cancel-options`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Erreur'); return d; })
      .then((d) => { setOpts(d); if (!d.paid) setMode('none'); })
      .catch((e) => setError(e.message));
  }, [booking.id, token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const q = opts?.paid ? `?refund_mode=${mode}` : '';
      const res = await fetch(`${API_URL}/api/tee-times/portal/bookings/${booking.id}${q}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Erreur lors de l'annulation");
      const r = d.refund || {};
      if (r.mode === 'credit') toast.success(`Réservation annulée — ${Number(r.amount).toFixed(2)} € crédités sur votre compte`);
      else if (r.mode === 'stripe') toast.success(`Réservation annulée — remboursement de ${Number(r.amount).toFixed(2)} € en cours`);
      else toast.success('Réservation annulée');
      onCancelled?.(d);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const deadlineTxt = opts?.deadline ? new Date(opts.deadline).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose} data-testid="portal-cancel-dialog">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-neutral-900">Annuler ma réservation</h3>
            <p className="text-xs text-neutral-500 mt-0.5">{booking.slot_date} à {booking.slot_time} · {booking.players_count} joueur{booking.players_count > 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100" data-testid="portal-cancel-close"><X size={16} /></button>
        </div>

        {error && <p className="text-sm text-red-600 mb-3" data-testid="portal-cancel-error">{error}</p>}
        {!opts && !error && <div className="flex items-center gap-2 text-sm text-neutral-500 py-4"><Loader2 size={14} className="animate-spin" /> Chargement…</div>}

        {opts && !opts.can_cancel && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-3 text-sm text-amber-800" data-testid="portal-cancel-too-late">
            <div className="flex items-center gap-2 font-semibold"><Clock size={15} /> Délai dépassé</div>
            <p className="text-xs mt-1">Les annulations et changements d'horaire en ligne sont possibles jusqu'à <strong>{opts.hours_before}h</strong> avant le départ{deadlineTxt ? ` (soit jusqu'au ${deadlineTxt})` : ''}. Merci de contacter l'accueil.</p>
          </div>
        )}

        {opts && opts.can_cancel && (
          <>
            {opts.paid ? (
              <>
                <p className="text-xs text-neutral-600 mb-2" data-testid="portal-cancel-paid-info">
                  Vous avez réglé <strong>{Number(opts.amount).toFixed(2)} €</strong>{opts.paid_via === 'stripe' ? ' en ligne' : ' à l\u2019accueil'}. Comment souhaitez-vous être remboursé ?
                </p>
                <div className="space-y-2 mb-3">
                  {opts.options.map((o) => {
                    const Icon = o.mode === 'stripe' ? CreditCard : Wallet;
                    const active = mode === o.mode;
                    return (
                      <button
                        key={o.mode}
                        type="button"
                        onClick={() => setMode(o.mode)}
                        className={`w-full text-left rounded-xl border-2 px-3 py-2.5 transition-colors ${active ? 'border-[#2E6A4C] bg-[#2E6A4C]/5' : 'border-neutral-200 hover:bg-neutral-50'}`}
                        data-testid={`portal-cancel-mode-${o.mode}`}
                      >
                        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800"><Icon size={15} style={{ color: '#2E6A4C' }} /> {o.mode === 'credit' ? 'Avoir sur mon compte client' : 'Remboursement sur ma carte'}</div>
                        <div className="text-[11px] text-neutral-500 mt-0.5">{o.mode === 'credit' ? `${Number(opts.amount).toFixed(2)} € disponibles immédiatement pour vos prochaines réservations ou à l'accueil` : `${Number(opts.amount).toFixed(2)} € recrédités via Stripe sur la carte utilisée (5 à 10 jours)`}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-xs text-neutral-600 mb-3">Confirmez-vous l'annulation de ce départ ?</p>
            )}
            <p className="text-[10px] text-neutral-400 mb-3">Annulation possible jusqu'à {opts.hours_before}h avant le départ{deadlineTxt ? ` (jusqu'au ${deadlineTxt})` : ''}.</p>
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 h-10 rounded-lg border text-sm font-medium text-neutral-700" data-testid="portal-cancel-back">Garder ma réservation</button>
              <button
                onClick={confirm}
                disabled={busy || (opts.paid && !mode)}
                className="flex-1 h-10 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
                data-testid="portal-cancel-confirm"
              >
                {busy && <Loader2 size={14} className="animate-spin" />} Annuler le départ
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
